/**
 * src/api/atticApi.ts — Domain-specific wrappers over the Harper Fabric REST
 * client. All transport, auth, and error translation happen in harperClient;
 * these functions preserve the legacy response envelopes (PaginatedResponse,
 * SingleResponse, MutationResponse) that existing hooks and components expect.
 *
 * Migration status: fully ported off the Fastify `/api/*` proxy layer.
 */

import { getOne, getCollection, post, put, patch, del, HarperError, HARPER_BASE_URL } from './harperClient';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ── Response types ──────────────────────────────────────────────────────

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

interface SingleResponse<T> {
  data: T;
}

interface MutationResponse {
  success: boolean;
}

// ── Crates ────────────────────────────────────────────────────────────────

function translateHarperError(err: unknown): never {
  if (err instanceof HarperError) throw new ApiError(err.status, err.message);
  throw err;
}

/**
 * Paginate a client-side array to mimic the legacy Fastify envelope.
 * Harper's Crate/Order collection endpoints don't accept limit/offset query
 * params yet (custom-Resource query-string access is an open item) — until
 * Stephen gives us the pattern, slice here.
 */
function paginate<T>(all: T[], limit: number, offset: number): PaginatedResponse<T> {
  return {
    data: all.slice(offset, offset + limit),
    total: all.length,
    limit,
    offset,
  };
}

export async function getCrates(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Record<string, unknown>>> {
  const limit = Math.min(params?.limit ?? 50, 200);
  const offset = params?.offset ?? 0;
  try {
    const res = await getCollection<Record<string, unknown>>('/Crate');
    let rows = res.data;
    if (params?.status) rows = rows.filter((b) => b.status === params.status);
    return paginate(rows, limit, offset);
  } catch (err) { translateHarperError(err); }
}

export async function getCrate(id: string): Promise<SingleResponse<Record<string, unknown>>> {
  try {
    const crate = await getOne<Record<string, unknown>>(`/Crate/${id}`);
    if (!crate) throw new ApiError(404, 'Crate not found');
    return { data: crate };
  } catch (err) { translateHarperError(err); }
}

export async function updateCrate(
  id: string,
  fields: Record<string, unknown>,
): Promise<MutationResponse> {
  try {
    // Crate.js's custom put() handler does partial-update semantics internally.
    await put(`/Crate/${id}`, fields);
    return { success: true };
  } catch (err) { translateHarperError(err); }
}

export async function orderCrate(): Promise<{ data: { crateId: string; orderId: string } }> {
  try {
    const res = await post<{ crateId: string; orderId: string }>('/OrderCrate', {});
    return { data: res };
  } catch (err) { translateHarperError(err); }
}

/**
 * Relinquish an empty crate back to the pool. Server enforces ownership +
 * at_home status; creates an empty_retrieval Order targeting this single
 * crate. Driver's empty_pickup scan detaches ownership and routes the
 * crate through the in-van inspection workflow (`/InspectEmptyCrate`).
 */
export async function returnCrate(
  crateId: string,
  opts?: { pickupMethod?: PickupMethod },
): Promise<{ data: { orderId: string } }> {
  try {
    const order = await post<{ uuid: string }>('/ReturnCrate', {
      crateId,
      pickupMethod: opts?.pickupMethod,
    });
    return { data: { orderId: order.uuid } };
  } catch (err) { translateHarperError(err); }
}

export async function getAdminCrates(params?: {
  status?: string | undefined;
  /** Server-side text search across uuid/crate_number/label/location/category + customer. */
  q?: string | undefined;
  /** 'exclude' drops pool crates (Inventory browser); 'only' keeps just pool crates (Pools). */
  pool?: 'exclude' | 'only' | undefined;
  /** Cap on returned rows. Omit for all (up to 100k). `truncated` flags when capped. */
  limit?: number | undefined;
}): Promise<{
  data: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  status_counts: Record<string, number>;
  customers: Record<string, { name: string; email: string }>;
  shelves: Record<string, { label: string; facility_id: string; storage_unit_id: string; grid_rows: number; grid_cols: number }>;
  units: Record<string, { unit_number: string; label: string; facility_id: string }>;
  facilities: Record<string, { code: string; label: string }>;
}> {
  const qs = new URLSearchParams();
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.q) qs.set('q', params.q);
  if (params?.pool) qs.set('pool', params.pool);
  if (params?.limit) qs.set('limit', String(params.limit));
  const path = qs.toString() ? `/AdminCrates?${qs.toString()}` : '/AdminCrates';
  try {
    const res = await getCollection<Record<string, unknown>>(path);
    type RawMaps = {
      total?: number;
      truncated?: boolean;
      status_counts?: Record<string, number>;
      customers?: Record<string, { name: string | null; email: string | null }>;
      shelves?: Record<string, { label: string | null; facility_id: string | null; storage_unit_id: string | null; grid_rows: number; grid_cols: number }>;
      units?: Record<string, { unit_number: string | null; label: string | null; facility_id: string | null }>;
      facilities?: Record<string, { code: string | null; label: string | null }>;
    };
    const maps = res as unknown as RawMaps;
    const customers: Record<string, { name: string; email: string }> = {};
    for (const [uuid, info] of Object.entries(maps.customers ?? {})) {
      customers[uuid] = { name: info.name ?? '', email: info.email ?? '' };
    }
    const shelves: Record<string, { label: string; facility_id: string; storage_unit_id: string; grid_rows: number; grid_cols: number }> = {};
    for (const [uuid, info] of Object.entries(maps.shelves ?? {})) {
      shelves[uuid] = {
        label: info.label ?? '', facility_id: info.facility_id ?? '',
        storage_unit_id: info.storage_unit_id ?? '',
        grid_rows: info.grid_rows, grid_cols: info.grid_cols,
      };
    }
    const units: Record<string, { unit_number: string; label: string; facility_id: string }> = {};
    for (const [uuid, info] of Object.entries(maps.units ?? {})) {
      units[uuid] = { unit_number: info.unit_number ?? '', label: info.label ?? '', facility_id: info.facility_id ?? '' };
    }
    const facilities: Record<string, { code: string; label: string }> = {};
    for (const [uuid, info] of Object.entries(maps.facilities ?? {})) {
      facilities[uuid] = { code: info.code ?? '', label: info.label ?? '' };
    }
    const data = (res.data ?? []) as Record<string, unknown>[];
    return {
      data,
      total: typeof maps.total === 'number' ? maps.total : data.length,
      truncated: maps.truncated ?? false,
      status_counts: maps.status_counts ?? {},
      customers, shelves, units, facilities,
    };
  } catch (err) { translateHarperError(err); }
}

/**
 * Paginated crate inventory browser — server-side status filter + substring
 * search + offset/limit, with per-page enrichment maps (see
 * resources/AdminInventory.js). Replaces the old getAdminCrates full-fleet load
 * for the Inventory page. (getAdminCrates stays for Storage occupancy + Pools,
 * which need the full set.)
 */
export async function getAdminInventory(params?: {
  status?: string | undefined;
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}): Promise<{
  data: Record<string, unknown>[];
  total: number | null;
  hasMore: boolean;
  offset: number;
  limit: number;
  searchTruncated: boolean;
  customers: Record<string, { name: string; email: string }>;
  shelves: Record<string, { label: string; facility_id: string; storage_unit_id: string; grid_rows: number; grid_cols: number }>;
  units: Record<string, { unit_number: string; label: string; facility_id: string }>;
  facilities: Record<string, { code: string; label: string }>;
}> {
  const limit = Math.min(params?.limit ?? 50, 200);
  const offset = Math.max(0, params?.offset ?? 0);
  const qs = new URLSearchParams();
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.q && params.q.trim()) qs.set('q', params.q.trim());
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  try {
    const res = await getCollection<Record<string, unknown>>(`/AdminInventory?${qs.toString()}`);
    type RawMaps = {
      total?: number | null; hasMore?: boolean; searchTruncated?: boolean;
      customers?: Record<string, { name: string | null; email: string | null }>;
      shelves?: Record<string, { label: string | null; facility_id: string | null; storage_unit_id: string | null; grid_rows: number; grid_cols: number }>;
      units?: Record<string, { unit_number: string | null; label: string | null; facility_id: string | null }>;
      facilities?: Record<string, { code: string | null; label: string | null }>;
    };
    const maps = res as unknown as RawMaps;
    const customers: Record<string, { name: string; email: string }> = {};
    for (const [uuid, info] of Object.entries(maps.customers ?? {})) customers[uuid] = { name: info.name ?? '', email: info.email ?? '' };
    const shelves: Record<string, { label: string; facility_id: string; storage_unit_id: string; grid_rows: number; grid_cols: number }> = {};
    for (const [uuid, info] of Object.entries(maps.shelves ?? {})) shelves[uuid] = { label: info.label ?? '', facility_id: info.facility_id ?? '', storage_unit_id: info.storage_unit_id ?? '', grid_rows: info.grid_rows, grid_cols: info.grid_cols };
    const units: Record<string, { unit_number: string; label: string; facility_id: string }> = {};
    for (const [uuid, info] of Object.entries(maps.units ?? {})) units[uuid] = { unit_number: info.unit_number ?? '', label: info.label ?? '', facility_id: info.facility_id ?? '' };
    const facilities: Record<string, { code: string; label: string }> = {};
    for (const [uuid, info] of Object.entries(maps.facilities ?? {})) facilities[uuid] = { code: info.code ?? '', label: info.label ?? '' };
    return {
      data: (res.data ?? []) as Record<string, unknown>[],
      total: maps.total ?? null,
      hasMore: !!maps.hasMore,
      offset, limit,
      searchTruncated: !!maps.searchTruncated,
      customers, shelves, units, facilities,
    };
  } catch (err) { translateHarperError(err); }
}

// ── Orders ──────────────────────────────────────────────────────────────

export async function getOrders(params?: {
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Record<string, unknown>>> {
  const limit = Math.min(params?.limit ?? 50, 200);
  const offset = params?.offset ?? 0;
  try {
    const res = await getCollection<Record<string, unknown>>('/Order');
    return paginate(res.data, limit, offset);
  } catch (err) { translateHarperError(err); }
}

/**
 * Route "Send in" → /SchedulePickup and "Bring home" / retrieval →
 * /ScheduleReturn. Body-casing maps the legacy snake_case payload to the
 * Resource's camelCase inputs.
 */
export type PickupMethod = 'porch_pickup' | 'doorbell';

export async function createOrder(payload: {
  type: string;
  crate_ids: string[];
  /** Exact-day booking. Mutually exclusive with window_id. */
  scheduled_date?: string | undefined;
  /** Pricing v2 flexible window. Mutually exclusive with scheduled_date. */
  window_id?: string | undefined;
  /**
   * The fee total the customer saw, in cents. REQUIRED on delivery/retrieval:
   * the server refuses a priced booking that doesn't state its price rather
   * than booking it free, and 409s if its own computation disagrees. Omitted
   * for pickups, which are free and stay on the pre-v2 shape.
   */
  displayed_fee_cents?: number | undefined;
  time_window?: string | undefined;
  pickup_address?: string | undefined;
  delivery_instructions?: string | undefined;
  pickup_method?: PickupMethod | undefined;
  /** Per-order override of Customer.gate_code. Omit to inherit the default. */
  gate_code?: string | undefined;
  /** Per-order override of Customer.buzzer_apartment. */
  buzzer_apartment?: string | undefined;
}): Promise<{ data: { orderId: string } }> {
  try {
    if (payload.type === 'pickup') {
      const order = await post<{ uuid: string }>('/SchedulePickup', {
        crateIds: payload.crate_ids,
        scheduledDate: payload.scheduled_date,
        timeWindow: payload.time_window,
        pickupAddress: payload.pickup_address ?? '',
        deliveryInstructions: payload.delivery_instructions,
        pickupMethod: payload.pickup_method,
        gateCode: payload.gate_code,
        buzzerApartment: payload.buzzer_apartment,
      });
      return { data: { orderId: order.uuid } };
    }
    if (payload.type === 'delivery' || payload.type === 'retrieval') {
      const order = await post<{ uuid: string }>('/ScheduleReturn', {
        crateIds: payload.crate_ids,
        // Send exactly one of these — the server 400s if both are present, so
        // they are omitted rather than sent as undefined.
        ...(payload.window_id
          ? { windowId: payload.window_id }
          : { scheduledDate: payload.scheduled_date }),
        displayedFeeCents: payload.displayed_fee_cents,
        timeWindow: payload.time_window,
        pickupAddress: payload.pickup_address ?? '',
        deliveryInstructions: payload.delivery_instructions,
        orderType: payload.type,
        pickupMethod: payload.pickup_method,
        gateCode: payload.gate_code,
        buzzerApartment: payload.buzzer_apartment,
      });
      return { data: { orderId: order.uuid } };
    }
    throw new ApiError(400, `Unknown order type: "${payload.type}"`);
  } catch (err) { translateHarperError(err); }
}

export async function updateOrder(
  id: string,
  fields: Record<string, unknown>,
): Promise<MutationResponse> {
  try {
    await put(`/Order/${id}`, fields);
    return { success: true };
  } catch (err) { translateHarperError(err); }
}


/** Per-completed-shift performance stats for a W-2 driver — no pay. */
export interface DriverShiftStat {
  shift_id: string;
  requested_hours: number;
  started_at: string;
  completed_at: string | null;
  /** Null on shifts completed before the 2026-07-28 denormalization backfill. */
  stops_count: number | null;
  crates_count: number | null;
  distance_km: number;
}

/**
 * Lifetime aggregates computed server-side over the driver's WHOLE history.
 * Prefer these over reducing `data` — `data` may be a page.
 */
export interface DriverStatsTotals {
  shifts: number;
  stops: number;
  distance_km: number;
}

export async function getDriverStats(
  driverId?: string,
): Promise<{ data: DriverShiftStat[]; totals: DriverStatsTotals | null }> {
  try {
    const path = driverId ? `/DriverStats/${driverId}` : '/DriverStats';
    const res = await getOne<{ data: DriverShiftStat[]; totals?: DriverStatsTotals }>(path);
    // null rather than undefined: exactOptionalPropertyTypes is on, and "the
    // backend didn't send totals" is a state callers must handle explicitly.
    return { data: res?.data ?? [], totals: res?.totals ?? null };
  } catch (err) { translateHarperError(err); }
}

export async function endShift(params?: { shiftId?: string }): Promise<{
  success: boolean;
  shift_id: string;
  status: 'completed';
  completed_at: string;
  stops_total: number;
  stops_completed: number;
  orders_released?: number;
  orders_completed?: number;
  seconds_elapsed: number;
}> {
  try {
    return await post('/EndShift', params ?? {});
  } catch (err) { translateHarperError(err); }
}

export async function getAdminOrders(params?: {
  status?: string | undefined;
  type?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}): Promise<{
  data: Record<string, unknown>[];
  total: number | null;
  hasMore: boolean;
  offset: number;
  limit: number;
  customers: Record<string, { name: string; email: string }>;
}> {
  // Server-side: indexed status/type filter + created_at sort + offset/limit
  // pagination (no client-side full-table load — see resources/AdminOrders.js).
  const limit = Math.min(params?.limit ?? 50, 200);
  const offset = Math.max(0, params?.offset ?? 0);
  const qs = new URLSearchParams();
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.type && params.type !== 'all') qs.set('type', params.type);
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  try {
    const res = await getCollection<Record<string, unknown>>(`/AdminOrders?${qs.toString()}`) as
      (Awaited<ReturnType<typeof getCollection<Record<string, unknown>>>> & {
        total?: number | null; hasMore?: boolean; customers?: Record<string, { name: string | null; email: string | null }>;
      });
    const customers: Record<string, { name: string; email: string }> = {};
    if (res.customers) {
      for (const [uuid, info] of Object.entries(res.customers)) {
        customers[uuid] = { name: info.name ?? '', email: info.email ?? '' };
      }
    }
    return {
      data: res.data ?? [],
      total: res.total ?? null,
      hasMore: !!res.hasMore,
      offset, limit, customers,
    };
  } catch (err) { translateHarperError(err); }
}

/**
 * Admin: cancel a pre-transit order. Reverts crate states server-side via
 * CancelOrder → lib/orderCancellation. Throws (translated) for non-cancellable
 * statuses (409) so the caller can surface the reason.
 */
export async function cancelAdminOrder(orderId: string): Promise<{
  success: boolean; orderId: string; from: string; to: string; revertedCrates: number;
}> {
  try {
    return await post('/CancelOrder', { orderId });
  } catch (err) { translateHarperError(err); }
}

/**
 * Admin: reschedule a pre-transit order (date + time window, optional pickup
 * address for pickups). Schedule-only — crate state is untouched server-side.
 */
export async function rescheduleAdminOrder(
  orderId: string,
  fields: { scheduledDate: string; timeWindow: string; pickupAddress?: string },
): Promise<{ success: boolean; orderId: string; scheduled_date: string; time_window: string; from: string; to: string }> {
  try {
    return await post('/RescheduleOrder', { orderId, ...fields });
  } catch (err) { translateHarperError(err); }
}

/**
 * Admin: pin an unclaimed pending order to a driver (reservation model — the
 * assignee sees it flagged in their pool and only they can plan it). Pass
 * driverId null to clear the assignment.
 */
export async function assignOrderDriver(
  orderId: string,
  driverId: string | null,
): Promise<{ success: boolean; orderId: string; assigned_driver_id: string | null }> {
  try {
    return await post('/AssignOrderDriver', { orderId, driverId });
  } catch (err) { translateHarperError(err); }
}

// ── Admin audit log ───────────────────────────────────────────────────────

export interface AdminAuditEntry {
  uuid: string;
  actor_id: string | null;
  actor_email: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  summary: string | null;
  metadata: string | null;
  created_at: string;
}

/** Admin: read the privileged-action audit trail (newest-first). Optionally
 *  scoped to one entity (entity_id) or one action type. */
export async function getAdminAuditLog(params?: { entityId?: string; action?: string }): Promise<{ data: AdminAuditEntry[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.entityId) qs.set('entity_id', params.entityId);
  if (params?.action) qs.set('action', params.action);
  const path = qs.toString() ? `/AdminAuditLog?${qs.toString()}` : '/AdminAuditLog';
  try {
    const res = await getCollection<AdminAuditEntry>(path);
    return { data: res.data, total: res.total };
  } catch (err) { translateHarperError(err); }
}

// ── Customers ───────────────────────────────────────────────────────────

/**
 * Migrated to Harper Fabric `/Me`. Wire-shape unchanged: the function still
 * returns `{ data: Customer }` so consumers (customerApi, hooks) don't notice.
 * Errors surface as the legacy ApiError shape.
 */
export async function getMyProfile(): Promise<SingleResponse<Record<string, unknown>>> {
  try {
    const me = await getOne<Record<string, unknown>>('/Me');
    if (!me) throw new ApiError(404, 'Customer not found');
    return { data: me };
  } catch (err) {
    if (err instanceof HarperError) throw new ApiError(err.status, err.message);
    throw err;
  }
}

export async function updateMyProfile(
  fields: Record<string, unknown>,
): Promise<MutationResponse> {
  try {
    await patch<{ success: boolean }>('/Me', fields);
    return { success: true };
  } catch (err) {
    if (err instanceof HarperError) throw new ApiError(err.status, err.message);
    throw err;
  }
}

// ── Shelves ─────────────────────────────────────────────────────────────

export interface FacilityRecord {
  id: string;
  uuid?: string;
  code: string;
  label: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  /** Cluster this facility serves (EP-DELIVERY-ROUTING-V1). Null for legacy. */
  cluster_id?: string | null;
  /** JSON-serialized hours per day, e.g. {"mon":"9-17",...}. */
  opening_hours?: string | null;
  /** Serviceability coverage radius in miles; null → server default (8mi). */
  service_radius_mi?: number | null;
  /** Driver-facing gate / door code into the facility (shared across units). */
  access_code?: string | null;
}

export interface ClusterRecord {
  id: string;
  uuid?: string;
  name: string;
  lat: number;
  lng: number;
  radius_mi: number;
}

export interface StorageUnitRecord {
  id: string;
  uuid?: string;
  facility_id: string;
  unit_number: string;
  unit_type: 'customer' | 'empty_pool';
  label: string;
  /** Driver-facing padlock code for this unit. */
  lock_code?: string | null;
}

export interface ShelfRecord {
  id: string;
  uuid?: string;
  facility_id: string;
  storage_unit_id: string;
  label: string;
  zone: string | null;
  aisle: string | null;
  row: string | null;
  grid_rows: number;
  grid_cols: number;
  qr_code: string;
  created_at: string;
  updated_at: string;
}

export async function getFacilities(): Promise<{ data: FacilityRecord[]; total: number }> {
  try {
    const res = await getCollection<FacilityRecord>('/Facility');
    return { data: res.data, total: res.total };
  } catch (err) { translateHarperError(err); }
}

export async function createFacility(payload: {
  code: string;
  label: string;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  cluster_id?: string | null;
  opening_hours?: string | null;
  service_radius_mi?: number | null;
  access_code?: string | null;
}): Promise<FacilityRecord> {
  try {
    return await post<FacilityRecord>('/Facility', payload);
  } catch (err) { translateHarperError(err); }
}

export async function updateFacility(
  uuid: string,
  payload: Partial<Omit<FacilityRecord, 'id' | 'uuid'>>,
): Promise<{ uuid: string }> {
  try {
    return await put<{ uuid: string }>(`/Facility/${uuid}`, payload);
  } catch (err) { translateHarperError(err); }
}

export async function deleteFacility(uuid: string): Promise<{ success: boolean; uuid: string }> {
  try {
    return await del<{ success: boolean; uuid: string }>(`/Facility/${uuid}`);
  } catch (err) { translateHarperError(err); }
}

export async function getClusters(): Promise<{ data: ClusterRecord[]; total: number }> {
  try {
    const res = await getCollection<ClusterRecord>('/Cluster');
    return { data: res.data, total: res.total };
  } catch (err) { translateHarperError(err); }
}

export async function createCluster(payload: {
  name: string;
  lat: number;
  lng: number;
  radius_mi: number;
}): Promise<ClusterRecord> {
  try {
    return await post<ClusterRecord>('/Cluster', payload);
  } catch (err) { translateHarperError(err); }
}

export async function updateCluster(
  uuid: string,
  payload: Partial<Omit<ClusterRecord, 'id' | 'uuid'>>,
): Promise<{ uuid: string }> {
  try {
    return await put<{ uuid: string }>(`/Cluster/${uuid}`, payload);
  } catch (err) { translateHarperError(err); }
}

export async function deleteCluster(uuid: string): Promise<{ success: boolean; uuid: string }> {
  try {
    return await del<{ success: boolean; uuid: string }>(`/Cluster/${uuid}`);
  } catch (err) { translateHarperError(err); }
}

// ── Empty retrieval (slice 7) ───────────────────────────────────────────

/**
 * Book an "empty_retrieval" Order — driver comes to pick up every crate
 * the customer currently has at_home. EP-DELIVERY-ROUTING-V1 slice 7.
 */
export async function bookEmptyRetrieval(payload: {
  scheduledDate: string;
  timeWindow: string;
  pickupAddress?: string;
}): Promise<{ uuid: string; requested_crate_count: number }> {
  try {
    return await post<{ uuid: string; requested_crate_count: number }>('/BookEmptyRetrieval', payload);
  } catch (err) { translateHarperError(err); }
}

/**
 * Driver inspection follow-up after an empty_pickup scan. Flips a crate
 * in 'inspecting' status to either 'available' (clean → reusable) or
 * 'needs_cleaning' (return to facility for ops).
 */
export async function inspectEmptyCrate(
  crateId: string,
  result: 'clean' | 'needs_cleaning',
): Promise<{ success: boolean; crateId: string; status: string }> {
  try {
    return await post<{ success: boolean; crateId: string; status: string }>(
      '/InspectEmptyCrate',
      { crateId, result },
    );
  } catch (err) { translateHarperError(err); }
}

export async function getStorageUnits(): Promise<{ data: StorageUnitRecord[]; total: number }> {
  try {
    const res = await getCollection<StorageUnitRecord>('/StorageUnit');
    return { data: res.data, total: res.total };
  } catch (err) { translateHarperError(err); }
}

export async function getShelves(): Promise<{ data: ShelfRecord[]; total: number }> {
  try {
    const res = await getCollection<ShelfRecord>('/Shelf');
    return { data: res.data, total: res.total };
  } catch (err) { translateHarperError(err); }
}

export async function createStorageUnit(payload: {
  facility_id: string;
  unit_number: string;
  label?: string;
  unit_type?: 'customer' | 'empty_pool';
  lock_code?: string | null;
}): Promise<StorageUnitRecord> {
  try {
    return await post<StorageUnitRecord>('/StorageUnit', payload);
  } catch (err) { translateHarperError(err); }
}

export async function updateStorageUnit(
  uuid: string,
  payload: Partial<Omit<StorageUnitRecord, 'id' | 'uuid' | 'facility_id'>>,
): Promise<{ uuid: string }> {
  try {
    return await put<{ uuid: string }>(`/StorageUnit/${uuid}`, payload);
  } catch (err) { translateHarperError(err); }
}

export async function deleteStorageUnit(uuid: string): Promise<{ success: boolean; uuid: string }> {
  try {
    return await del<{ success: boolean; uuid: string }>(`/StorageUnit/${uuid}`);
  } catch (err) { translateHarperError(err); }
}

export async function createShelf(payload: {
  storage_unit_id: string;
  label: string;
  zone?: string;
  aisle?: string;
  row?: string;
  grid_rows?: number;
  grid_cols?: number;
}): Promise<{ data: ShelfRecord }> {
  try {
    const shelf = await post<ShelfRecord>('/Shelf', payload);
    return { data: shelf };
  } catch (err) { translateHarperError(err); }
}

/**
 * A physical crate position, as it will be printed on a beam.
 *
 * `number` is frozen server-side at generation and never recomputed — it is
 * what the beam label says, so it must not follow a shelf rename. See the Slot
 * table comment in schemas/schema.graphql.
 */
/** One printable slot, as GenerateUnitSlots returns it. */
export interface SlotPrintRow {
  code: string;
  number: number;
  shelf_id: string;
  slot_row: number;
  slot_col: number;
  bay_index: number | null;
  status: string;
}

export interface SlotRecord {
  id: string;
  uuid?: string;
  storage_unit_id: string;
  shelf_id: string;
  slot_row: number;
  slot_col: number;
  number: number;
  bay_index: number | null;
  status: string | null;
  created_at?: string | null;
  retired_at?: string | null;
}

/**
 * Mint Slot rows for any of a unit's positions that lack one, and return the
 * unit's FULL set ready to print.
 *
 * There is no `/Slot` GET to read them from: Slot carries no @export, because
 * Harper's permissions are table-level and an auto-generated route would expose
 * every slot in every facility to any role holding a table grant — the same
 * reason @export is off ShelfAssignmentLog. So the read rides back on this
 * call, behind its admin check.
 */
export async function generateUnitSlots(unitId: string): Promise<{
  created: number; existing: number; total: number; slots: SlotPrintRow[];
}> {
  try {
    return await post<{ created: number; existing: number; total: number; slots: SlotPrintRow[] }>(
      '/GenerateUnitSlots', { unitId },
    );
  } catch (err) { translateHarperError(err); }
}

/* ── Security seals ─────────────────────────────────────────────────────────
 * Seals are PRE-REGISTERED: a serial exists as a `printed` Seal row before any
 * label is physically printed, and /SealCrate refuses anything not in that
 * registry. Minting is therefore the cheap, safe half — burn serials freely —
 * while printing against an unregistered serial would produce a tie our own
 * server rejects. See resources/lib/sealSerial.js.
 */

/** One minted seal, as SealBatches returns it. */
export interface SealRow {
  serial: string;
  status: string;
  crate_id: string | null;
}

/** A print run, summarised for the batch list. */
export interface SealBatchSummary {
  batch_id: string;
  total: number;
  printed: number;
  applied: number;
  voided: number;
  first_serial: string | null;
  last_serial: string | null;
  printed_at: string | null;
}

/** Mint `count` PAIRS of seals. One serial = two labels = one crate. */
export async function generateSeals(count: number): Promise<{
  batch_id: string; minted: number; printed_at: string; serials: string[];
}> {
  try {
    return await post<{ batch_id: string; minted: number; printed_at: string; serials: string[] }>(
      '/GenerateSeals', { count },
    );
  } catch (err) { translateHarperError(err); }
}

/** List every print run, newest first. */
export async function listSealBatches(): Promise<{ batches: SealBatchSummary[] }> {
  try {
    return await post<{ batches: SealBatchSummary[] }>('/SealBatches', {});
  } catch (err) { translateHarperError(err); }
}

/**
 * Re-fetch one batch to reprint it. Reprint exists because the failure mode is
 * physical — a jam, a misfeed, a roll running out — and minting a fresh batch
 * would burn a second block of serials while leaving the first registered but
 * never applied.
 */
export async function getSealBatch(batchId: string): Promise<{
  batch: SealBatchSummary; serials: SealRow[];
}> {
  try {
    return await post<{ batch: SealBatchSummary; serials: SealRow[] }>(
      '/SealBatches', { batchId },
    );
  } catch (err) { translateHarperError(err); }
}

export async function updateShelf(
  uuid: string,
  payload: Partial<Pick<ShelfRecord, 'label' | 'grid_rows' | 'grid_cols'>>,
): Promise<{ uuid: string }> {
  try {
    return await put<{ uuid: string }>(`/Shelf/${uuid}`, payload);
  } catch (err) { translateHarperError(err); }
}

export async function deleteShelf(id: string): Promise<{ success: true }> {
  try {
    await del<void>(`/Shelf/${id}`);
    return { success: true };
  } catch (err) { translateHarperError(err); }
}

export async function getShelf(id: string): Promise<{ data: ShelfRecord | null }> {
  try {
    const shelf = await getOne<ShelfRecord>(`/Shelf/${id}`);
    return { data: shelf };
  } catch (err) { translateHarperError(err); }
}

// ── Integrity (WS3) ─────────────────────────────────────────────────────

export interface CrateExceptionRecord {
  id: string;
  uuid?: string;
  crate_id: string;
  reason: string;
  severity: 'info' | 'warning' | 'critical';
  notes: string | null;
  raised_by: string;
  raised_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  status: 'open' | 'resolved' | 'acknowledged';
}

export interface AdminStats {
  orders_pending: number;
  orders_in_transit: number;
  crates_stored: number;
  crates_in_transit: number;
  shifts_active: number;
  exceptions_open: number;
  generated_at: string;
}

export async function getAdminStats(): Promise<AdminStats> {
  try {
    const res = await getOne<AdminStats>('/AdminStats');
    if (!res) throw new ApiError(500, 'No stats returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

// ── Operations hub rollup ─────────────────────────────────────────────────

export interface OpsBucket<T> { items: T[]; total: number }
export interface OpsUnassignedOrder { uuid: string; type: string; customer_id: string | null; customer_name: string | null; scheduled_date: string | null; time_window: string | null }
export interface OpsNeedsReschedule { uuid: string; type: string; customer_id: string | null; customer_name: string | null }
export interface OpsOpenException { uuid: string; reason: string | null; severity: string | null; crate_id: string | null; customer_id: string | null; customer_name: string | null }
export interface OpsLowPool { uuid: string; label: string | null; available: number; low_threshold: number }
export interface OpsPastDue { uuid: string; name: string | null; email: string | null; past_due_since: string | null }
export interface OpsActiveShift { shift_id: string; driver_id: string | null; driver_name: string | null; started_at: string | null; planned_stops_count: number }

export interface AdminOpsBoard {
  generated_at: string;
  stats: Omit<AdminStats, 'generated_at'>;
  worklist: {
    unassigned_orders: OpsBucket<OpsUnassignedOrder>;
    needs_reschedule: OpsBucket<OpsNeedsReschedule>;
    open_exceptions: OpsBucket<OpsOpenException>;
    low_pools: OpsBucket<OpsLowPool>;
    past_due: OpsBucket<OpsPastDue>;
  };
  active_shifts: OpsBucket<OpsActiveShift>;
}

/** Operations hub: one call → KPIs + the "what needs me now" worklist + active shifts. */
export async function getAdminOpsBoard(): Promise<AdminOpsBoard> {
  try {
    const res = await getOne<AdminOpsBoard>('/AdminOpsBoard');
    if (!res) throw new ApiError(500, 'No ops board returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

// ── Driver stop notes (dispatch review queue) ─────────────────────────────

export interface AdminStopNote {
  uuid: string;
  complex_key: string;
  address_raw: string | null;
  customer_id: string | null;
  driver_id: string | null;
  driver_name: string | null;
  body: string;
  published_body: string | null;
  status: 'pending' | 'published' | 'archived';
  created_at: string | null;
  reviewed_at: string | null;
}

export interface AdminStopNotesResponse {
  data: AdminStopNote[];
  total: number;
  counts: { pending: number; published: number; archived: number };
}

/** Driver-authored location notes awaiting review (or by status). */
export async function getAdminStopNotes(status: 'pending' | 'published' | 'archived' | 'all' = 'pending'): Promise<AdminStopNotesResponse> {
  try {
    const res = await getOne<AdminStopNotesResponse>(`/AdminStopNotes?status=${status}`);
    if (!res) throw new ApiError(500, 'No stop notes returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

/** Publish (optionally rewritten), edit a published note, or archive. Audited. */
export async function actOnStopNote(
  uuid: string,
  action: 'publish' | 'archive' | 'edit',
  body?: string,
): Promise<{ success: boolean; status: string }> {
  try {
    return await post<{ success: boolean; status: string }>('/AdminStopNotes', body ? { uuid, action, body } : { uuid, action });
  } catch (err) { translateHarperError(err); }
}

// ── Real storage capacity ─────────────────────────────────────────────────

export interface CapacityFacility { uuid: string; code: string | null; label: string | null; capacity: number; occupied: number; pct: number }
export interface AdminCapacity {
  generated_at: string;
  total: { capacity: number; occupied: number; available: number; pct: number };
  facilities: CapacityFacility[];
}

/** Real per-facility + system storage capacity (sum of customer-unit shelf slots
 *  vs. stored crates). Replaces the hardcoded ceiling. */
export async function getAdminCapacity(): Promise<AdminCapacity> {
  try {
    const res = await getOne<AdminCapacity>('/AdminCapacity');
    if (!res) throw new ApiError(500, 'No capacity returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

// ── Revenue snapshot ──────────────────────────────────────────────────────

export interface AdminRevenue {
  generated_at: string;
  /** 'stripe' — revenue is computed live from Stripe, the system of record. */
  source?: string;
  /** false when STRIPE_SECRET_KEY isn't set (graceful zeros instead of an error). */
  stripe_configured?: boolean;
  currency?: string;
  active_subscribers: number;
  past_due_subscribers?: number;
  mrr: number;
  arpu: number;
  plan_mix: { plan: string; count: number; mrr?: number }[];
}

/** Real MRR / active subscribers / ARPU + plan mix from the Subscription mirror. */
export async function getAdminRevenue(): Promise<AdminRevenue> {
  try {
    const res = await getOne<AdminRevenue>('/AdminRevenue');
    if (!res) throw new ApiError(500, 'No revenue returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

// ── Admin pricing (fee calendar, audited) ─────────────────────────────────

export interface AdminPricingConfig {
  /** Per CRATE, not per visit (pricing v3). */
  base_crate_fee_cents: number;
  /** Premium ADDED for an exact day, flat per visit. */
  exact_day_adder_cents: number;
  default_peak_adder_cents: number;
  return_booked_credit_cents: number;
  free_after_months: number;
  truckload_crates: number;
  booking_horizon_days: number;
  pickup_lead_business_days: number;
  peak_pickup_lead_business_days: number;
  updated_at?: string | null;
}
export interface AdminPricingWindow {
  uuid: string;
  start_date: string;
  end_date: string;
  label: string | null;
  peak: boolean;
  /** Per-visit peak adder; null → PricingConfig.default_peak_adder_cents. */
  adder_cents: number | null;
  active: boolean;
  updated_at?: string | null;
}
export interface AdminPricingData {
  config: AdminPricingConfig;
  windows: AdminPricingWindow[];
  generated_at: string;
}
/** Editable DeliveryWindow fields — dates are generated, deactivate instead. */
export type AdminPricingWindowPatch = Partial<Pick<AdminPricingWindow, 'label' | 'peak' | 'adder_cents' | 'active'>>;

/** Admin: pricing config + the FULL window catalog (inactive/past included). */
export async function getAdminPricing(): Promise<AdminPricingData> {
  try {
    const res = await getOne<AdminPricingData>('/AdminPricing');
    if (!res) throw new ApiError(500, 'No pricing data returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

/** Admin: patch whitelisted PricingConfig fields (server validates + audits). */
export async function updatePricingConfig(
  fields: Partial<AdminPricingConfig>,
  reason: string,
): Promise<{ ok: boolean; config: AdminPricingConfig }> {
  try {
    return await post<{ ok: boolean; config: AdminPricingConfig }>('/AdminPricing', { action: 'update_config', fields, reason });
  } catch (err) { translateHarperError(err); }
}

/** Admin: patch a delivery window (label / peak / adder / active — audited). */
export async function updatePricingWindow(
  uuid: string,
  fields: AdminPricingWindowPatch,
  /** Required by the server whenever the patch touches peak, adder or active. */
  reason?: string,
): Promise<{ ok: boolean; window: AdminPricingWindow }> {
  try {
    return await post<{ ok: boolean; window: AdminPricingWindow }>('/AdminPricing', { action: 'update_window', uuid, fields, reason });
  } catch (err) { translateHarperError(err); }
}

export interface ExceptionOwner { customer_id: string; customer_name: string | null }

export async function listExceptions(
  status: 'open' | 'resolved' | 'acknowledged' | 'all' = 'open',
): Promise<{ data: CrateExceptionRecord[]; total: number; owners: Record<string, ExceptionOwner> }> {
  try {
    const res = await getCollection<CrateExceptionRecord>(`/CrateException?status=${encodeURIComponent(status)}`) as
      (Awaited<ReturnType<typeof getCollection<CrateExceptionRecord>>> & { owners?: Record<string, ExceptionOwner> });
    return { data: res.data, total: res.total, owners: res.owners ?? {} };
  } catch (err) { translateHarperError(err); }
}

export async function resolveException(
  exceptionId: string,
  status: 'resolved' | 'acknowledged' = 'resolved',
): Promise<{ success: true }> {
  try {
    await patch(`/CrateException/${exceptionId}`, { status });
    return { success: true };
  } catch (err) { translateHarperError(err); }
}

export async function flagCrate(
  crateId: string,
  reason: string,
  opts?: { severity?: 'info' | 'warning' | 'critical' | undefined; notes?: string | undefined },
): Promise<{ data: CrateExceptionRecord }> {
  try {
    const res = await post<{ exception: CrateExceptionRecord }>('/FlagCrate', {
      crateId, reason,
      severity: opts?.severity,
      notes: opts?.notes,
    });
    return { data: res.exception };
  } catch (err) { translateHarperError(err); }
}

export async function runSweep(
  opts?: { dryRun?: boolean; ageDays?: number },
): Promise<{ dryRun: boolean; written?: number; skipped?: number; proposed?: unknown[]; count?: number; writtenSample?: unknown[] }> {
  try {
    return await post('/SweepIntegrity', { dryRun: opts?.dryRun, ageDays: opts?.ageDays });
  } catch (err) { translateHarperError(err); }
}

// ── Shifts / routing (Phase A) ─────────────────────────────────────────

export type ShiftHours = 1 | 4 | 8;

export interface RouteStopSummary {
  order_id: string | null;
  crate_id: string | null;
  action: 'pickup' | 'delivery' | 'facility_return' | 'facility_load' | 'empty_drop' | 'empty_pickup';
  lat: number;
  lng: number;
  customer_id: string | null;
  sequence: number;
  distance_km_from_prev: number;
  eta_minutes: number;
  van_load_after: number;
  time_window?: string | null;
}

export interface ShiftPlanSummary {
  preview: boolean;
  shift_id?: string;
  facility: { id: string; lat: number; lng: number; label: string };
  hours: ShiftHours | null;
  orderIds?: string[];
  initial_load: number;
  stops: RouteStopSummary[];
  total_stops: number;
  total_distance_km: number;
  estimated_minutes: number;
}

/** Pending order with customer info for driver selection. */
export interface PendingOrder {
  uuid: string;
  type: 'pickup' | 'delivery' | 'retrieval';
  status: string;
  customer_id: string;
  customer_name: string;
  customer_address: string;
  crate_ids: string[];
  /** Count of empty crates requested — set for new-crate deliveries; null otherwise. */
  requested_crate_count: number | null;
  /** Display count — `requested_crate_count` for new-crate orders, otherwise crate_ids.length. */
  crate_count: number;
  time_window: string | null;
  scheduled_date: string | null;
  pickup_method: string | null;
  created_at: string;
}

/** Fetch all unclaimed orders eligible for driver pickup. */
export async function getPendingOrders(): Promise<{ data: PendingOrder[] }> {
  try {
    return await getCollection<PendingOrder>('/PendingOrders');
  } catch (err) { translateHarperError(err); }
}

/**
 * Preview a run route. Accepts orderIds (new) or hours (legacy).
 */
export async function planShift(params: {
  orderIds?: string[];
  hours?: ShiftHours;
  driverId?: string;
}): Promise<ShiftPlanSummary> {
  try {
    return await post<ShiftPlanSummary>('/PlanShift', params);
  } catch (err) { translateHarperError(err); }
}

export interface AcceptShiftResult extends ShiftPlanSummary {
  success: boolean;
  shift_id: string;
  reservations_made: number;
}

/**
 * Commit a run. Accepts orderIds (new) or hours (legacy).
 */
export async function acceptShift(params: {
  orderIds?: string[];
  hours?: ShiftHours;
  driverId?: string;
}): Promise<AcceptShiftResult> {
  try {
    return await post<AcceptShiftResult>('/AcceptShift', params);
  } catch (err) { translateHarperError(err); }
}

export async function cancelShift(params?: { shiftId?: string }): Promise<{
  success: boolean;
  shift_id: string;
  status: 'cancelled';
  cancelled_at: string;
  reservations_released: number;
  orders_released: number;
}> {
  try {
    return await post('/CancelShift', params ?? {});
  } catch (err) { translateHarperError(err); }
}

// ── Admin: shift scheduling + driver hours ─────────────────────────────
//
// Two different things share one endpoint because an operator asks about them
// in one breath ("who is on Tuesday, and how many hours did last week run"):
//
//   ScheduledShift — the PLAN. Created by an operator ahead of time and able to
//                    exist with no driver on it. `open` is the load-bearing
//                    state: it is the company saying it needs someone there.
//   Shift          — the EXECUTION record, created at AcceptShift. Its
//                    started_at / completed_at pair is the only hours figure
//                    that exists anywhere in the product.

export type ScheduledShiftStatus = 'open' | 'assigned' | 'cancelled';
export type ShiftWorkType = 'route' | 'warehouse' | 'both';

export interface ScheduledShiftRow {
  id: string;
  /** Service day, YYYY-MM-DD. */
  date: string | null;
  starts_at: string | null;
  ends_at: string | null;
  /** Null = unstaffed. This is the gap an operator is looking for. */
  driver_id: string | null;
  driver_name: string | null;
  work_type: ShiftWorkType;
  status: ScheduledShiftStatus;
  notes: string | null;
  created_at: string | null;
}

/**
 * How a worked shift's duration is known. Payroll may sum `exact` and nothing
 * else — the other three are the honest ways of saying "not a number yet".
 *
 *  open          still running; it has no end because it has not ended
 *  unknown       closed, but a timestamp is missing or reversed
 *  needs_review  closed by StaleShiftSweep, so completed_at is the SWEEP'S
 *                clock rather than the hour the driver stopped. One production
 *                shift sat active 33 days; that span is recorded, not payable.
 */
export type ShiftDurationBasis = 'exact' | 'open' | 'unknown' | 'needs_review';

export interface WorkedShiftRow {
  shift_id: string;
  driver_id: string | null;
  driver_name: string | null;
  status: string | null;
  /** The day the shift started, YYYY-MM-DD, so it lines up with the schedule. */
  service_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  /** Null whenever the duration is not known. Never an estimate. */
  duration_seconds: number | null;
  duration_basis: ShiftDurationBasis;
  stops_count: number | null;
  planned_stops_count: number | null;
  total_distance_km: number | null;
}

export interface ShiftRosterDriver {
  uuid: string;
  name: string | null;
  email: string | null;
  role: string | null;
}

export interface AdminShiftsResponse {
  ok: boolean;
  today: string;
  from: string;
  to: string;
  drivers: ShiftRosterDriver[];
  scheduled: ScheduledShiftRow[];
  worked: WorkedShiftRow[];
  /** The Shift read hit its cap — hours in this window are incomplete. */
  truncated: boolean;
  /** False when the sweep-audit read failed: no row can be flagged for review. */
  sweep_flags_available: boolean;
}

/** GET /AdminShifts — the schedule and the worked shifts for one date range. */
export async function getAdminShifts(params: {
  from: string;
  to: string;
}): Promise<AdminShiftsResponse> {
  try {
    const qs = new URLSearchParams({ from: params.from, to: params.to }).toString();
    const res = await getOne<AdminShiftsResponse>(`/AdminShifts?${qs}`);
    if (!res) throw new ApiError(500, 'No shift data returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

/**
 * Create one staffed block, or `count` unstaffed ones. `startsAt` / `endsAt`
 * are built on the operator's machine, which is the only one that knows the
 * market's timezone; `date` is their local service day.
 */
export async function createScheduledShifts(params: {
  date: string;
  startsAt: string;
  endsAt: string;
  driverId?: string | null;
  workType?: ShiftWorkType;
  count?: number;
  notes?: string | null;
}): Promise<{ success: boolean; created: string[]; count: number }> {
  try {
    return await post('/AdminShifts', { action: 'create', ...params });
  } catch (err) { translateHarperError(err); }
}

/** Staff an open block, or hand `driverId: null` to empty a staffed one. */
export async function assignScheduledShift(params: {
  id: string;
  driverId: string | null;
}): Promise<{ success: boolean; id: string; driver_id: string | null; status: ScheduledShiftStatus }> {
  try {
    return await post('/AdminShifts', { action: 'assign', ...params });
  } catch (err) { translateHarperError(err); }
}

export async function cancelScheduledShift(params: { id: string }): Promise<{
  success: boolean; id: string; status: 'cancelled';
}> {
  try {
    return await post('/AdminShifts', { action: 'cancel', ...params });
  } catch (err) { translateHarperError(err); }
}

export type DeferStopReason = 'not_home' | 'crates_not_ready' | 'couldnt_access' | 'felt_unsafe';

/**
 * POST /DeferStop — driver marks a stop as failed (customer not home,
 * crates weren't ready, couldn't access, felt unsafe). Server flips the
 * order to `needs_reschedule`, writes a customer notification, and returns
 * deferred_at so the client can optimistically update the stop in local
 * state. scope 'visit' defers every open stop for the same customer at the
 * same location (mobile skip button semantics).
 */
export async function deferStop(params: {
  stopId: string;
  reason: DeferStopReason;
  scope?: 'stop' | 'visit';
}): Promise<{
  success: boolean;
  alreadyDeferred: boolean;
  deferredCount?: number;
  stop: { uuid: string; deferred_at: string; deferred_reason: DeferStopReason };
}> {
  try {
    return await post('/DeferStop', params);
  } catch (err) { translateHarperError(err); }
}

/**
 * POST /ReorderStops — driver/admin manually re-orders the pending stops on
 * an active shift. `stopOrder` is the COMPLETE list of the shift's RouteStop
 * UUIDs in the new desired order; the server rewrites each stop's `sequence`
 * and enforces that completed + warehouse stops stay pinned (facility_return
 * stays last). Returns the new (uuid, sequence) pairs so the client can
 * reconcile local state.
 */
export async function reorderStops(params: {
  stopOrder: string[];
  shiftId?: string;
}): Promise<{
  success: boolean;
  shift_id: string;
  stops: Array<{ uuid: string; sequence: number }>;
}> {
  try {
    return await post('/ReorderStops', params);
  } catch (err) { translateHarperError(err); }
}

/**
 * POST /MarkEnRoute — driver taps "I'm heading to you" for a customer stop.
 * Server moves the Order to en_route and fires the "driver on the way"
 * notification (email + push) to the customer, once per order (idempotent on
 * an already-en_route order).
 */
export async function markEnRoute(params: {
  stopId: string;
}): Promise<{
  success: boolean;
  alreadyEnRoute: boolean;
  order_id: string;
}> {
  try {
    return await post('/MarkEnRoute', params);
  } catch (err) { translateHarperError(err); }
}

/**
 * POST /PostDriverLocation — driver app publishes its current GPS fix (Task 11).
 * Best-effort: callers should swallow failures (a missed publish just lags the
 * admin map). Identity is taken server-side from auth, not the body.
 */
export async function postDriverLocation(params: {
  lat: number;
  lng: number;
  accuracy_m?: number;
}): Promise<{ success: boolean }> {
  try {
    return await post('/PostDriverLocation', params);
  } catch (err) { translateHarperError(err); }
}

/** One active driver's live position, as returned by /ActiveDriverLocations. */
export interface ActiveDriverLocation {
  driver_id: string;
  driver_name: string | null;
  shift_id: string | null;
  lat: number;
  lng: number;
  accuracy_m: number | null;
  updated_at: string;
  age_seconds: number;
}

/**
 * GET /ActiveDriverLocations — admin dispatch reads live positions of drivers
 * currently on an active shift (fresh fixes only). The admin live map subscribes
 * to the DriverLocation table over WS and calls this to refetch on each change.
 */
export async function getActiveDriverLocations(): Promise<{
  drivers: ActiveDriverLocation[];
  generated_at: string;
}> {
  try {
    const res = await getOne<{ drivers: ActiveDriverLocation[]; generated_at: string }>(
      '/ActiveDriverLocations',
    );
    return res ?? { drivers: [], generated_at: new Date().toISOString() };
  } catch (err) { translateHarperError(err); }
}

export interface ValidateCrateScanMismatch {
  success: false;
  // Stow is unit-level now: 'wrong_unit' (scanned a shelf outside the crate's
  // unit, or shelf unrecognized) and 'shelf_full' (no free slot on that shelf).
  // 'wrong_shelf' retained for back-compat with any older clients/tests.
  mismatch: 'wrong_unit' | 'shelf_full' | 'wrong_shelf';
  message: string;
  expected: {
    unit_id?: string | null;
    unit_number: string | null;
    unit_lock_code?: string | null;
    facility_code: string | null;
    facility_label: string | null;
    facility_access_code?: string | null;
    // Legacy shelf-level fields (only present on old 'wrong_shelf' responses).
    shelf_id?: string;
    shelf_label?: string | null;
    slot_row?: number | null;
    slot_col?: number | null;
  };
}

export interface ValidateCrateScanSuccess {
  success: true;
  stop: { uuid: string; sequence: number; action: 'pickup' | 'delivery' | 'facility_return' };
  crateStatus: 'in_transit' | 'at_home' | 'stored' | null;
  /**
   * Whether the server actually stamped `stop.uuid.actual_arrival`. For
   * facility_return stow scans this is `false` when one or more pickup
   * crates from the shift are still `in_transit` — the crate was stowed but
   * the terminal stop is held open until every crate is shelved. Clients
   * must NOT optimistically mark the stop as visited when this is false.
   */
  stopStamped?: boolean;
  nextStop: {
    uuid: string;
    sequence: number;
    action: 'pickup' | 'delivery' | 'facility_return' | 'facility_load' | 'empty_drop' | 'empty_pickup';
    crate_id: string | null;
    /** The number printed on the crate — what a driver compares to the sticker. */
    crate_number?: string | null;
    order_id: string | null;
    lat: number;
    lng: number;
  } | null;
}

export type ValidateCrateScanResult = ValidateCrateScanSuccess | ValidateCrateScanMismatch;

export async function validateCrateScan(params: {
  crateId: string;
  shelfId?: string;
}): Promise<ValidateCrateScanResult> {
  try {
    return await post<ValidateCrateScanResult>('/ValidateCrateScan', params);
  } catch (err) { translateHarperError(err); }
}

export interface ActiveShiftRecord {
  uuid: string;
  driver_id: string;
  requested_hours: number;
  started_at: string;
  completed_at: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'planning';
  planned_stops_count: number;
  total_distance_km: number;
  crates_taken?: number | null;
  crates_delivered?: number | null;
  crates_returned?: number | null;
  pool_id_taken_from?: string | null;
}

export interface RouteStopRecord {
  uuid: string;
  shift_id: string;
  sequence: number;
  order_id: string | null;
  crate_id: string | null;
  /** The number printed on the crate — what a driver compares to the sticker. */
  crate_number?: string | null;
  action: 'pickup' | 'delivery' | 'facility_return' | 'facility_load' | 'empty_drop' | 'empty_pickup';
  estimated_arrival: string;
  actual_arrival: string | null;
  lat: number;
  lng: number;
  van_load_after: number;
  customer_name?: string | null;
  customer_address?: string | null;
  customer_phone?: string | null;
  pickup_method?: 'porch_pickup' | 'doorbell' | null;
  // Driver-visible delivery info, already resolved server-side per the
  // `order.X ?? customer.X` rule. Null when both levels are unset — the
  // stop-card UI simply doesn't render the chip in that case.
  gate_code?: string | null;
  buzzer_apartment?: string | null;
  special_instructions?: string | null;
  // Deferred-stop fields — driver marked this stop failed. Treated as
  // "done" for active-group advancement; UI shows a "missed" chip.
  deferred_at?: string | null;
  deferred_reason?: DeferStopReason | null;
  // facility_load-only: where to find the crate in the facility
  source_shelf_id?: string | null;
  source_storage_unit_id?: string | null;
  source_slot_row?: number | null;
  source_slot_col?: number | null;
  source_shelf_label?: string | null;
  /** Unit-sequential slot number (Slot 1..20 shelf A, 21..40 shelf B…). */
  source_slot_number?: number | null;
  source_unit_number?: string | null;
  source_unit_label?: string | null;
  source_unit_lock_code?: string | null;
  source_facility_code?: string | null;
  source_facility_label?: string | null;
  source_facility_address?: string | null;
  source_facility_access_code?: string | null;
}

export interface StowPlanItem {
  crate_id: string;
  // Null for a pre-restow undelivered crate whose load stop had no source shelf.
  shelf_id: string | null;
  shelf_label: string | null;
  shelf_qr: string | null;
  /** Undelivered outbound crate going back on the shelf (its delivery was
   * deferred). No reservation — any open shelf works; the unit is a hint. */
  restow?: boolean;
  // Stow is unit-level: the driver drops crates on any open shelf in this unit.
  unit_id: string | null;
  unit_label: string | null;
  unit_number: string | null;
  unit_lock_code: string | null;
  facility_code: string | null;
  facility_label: string | null;
  facility_access_code: string | null;
  facility_address: string | null;
  // Legacy advisory only (server auto-assigns the real slot at scan time).
  /** The number printed on the crate, e.g. "A000123". Null on pre-counter crates. */
  crate_number?: string | null;
  slot_row: number | null;
  slot_col: number | null;
  stowed: boolean;
}

export async function getActiveShift(driverId?: string): Promise<{
  shift: ActiveShiftRecord | null;
  stops: RouteStopRecord[];
  stowPlan?: StowPlanItem[];
}> {
  try {
    const path = driverId ? `/ActiveShift/${driverId}` : '/ActiveShift';
    const res = await getOne<{ shift: ActiveShiftRecord | null; stops: RouteStopRecord[]; stowPlan?: StowPlanItem[] }>(path);
    return res ?? { shift: null, stops: [] };
  } catch (err) { translateHarperError(err); }
}



// --- Empty-crate pool ---------------------------------------------------------

export interface EmptyCratePool {
  uuid: string;
  id?: string;                // normalized alias from Harper
  facility_id: string | null;
  label: string | null;
  count: number | null;
  low_threshold: number | null;
  created_at?: string | null;
}

export async function getPools(): Promise<{ data: EmptyCratePool[] }> {
  try {
    const res = await getOne<{ data: EmptyCratePool[] }>('/EmptyCratePool');
    return { data: res?.data ?? [] };
  } catch (err) { translateHarperError(err); }
}

export async function poolTake(params: { poolId: string; count: number }): Promise<{
  success: boolean;
  pool: { uuid: string; count: number; label: string | null };
  shift: { uuid: string; crates_taken: number };
}> {
  try {
    return await post('/PoolTake', params);
  } catch (err) { translateHarperError(err); }
}

export async function poolReturn(params: { poolId: string; count: number }): Promise<{
  success: boolean;
  pool: { uuid: string; count: number; label: string | null };
  shift: { uuid: string; crates_returned: number; outstanding: number };
}> {
  try {
    return await post('/PoolReturn', params);
  } catch (err) { translateHarperError(err); }
}

export async function orderNewCrates(payload: {
  requested_crate_count: number;
  scheduled_date: string;
  time_window: string;
  pickup_address?: string | undefined;
  delivery_instructions?: string | undefined;
  pickup_method?: PickupMethod | undefined;
}): Promise<{ data: { orderId: string } }> {
  try {
    const order = await post<{ uuid: string }>('/OrderNewCrates', {
      requestedCrateCount: payload.requested_crate_count,
      scheduledDate: payload.scheduled_date,
      timeWindow: payload.time_window,
      pickupAddress: payload.pickup_address ?? '',
      deliveryInstructions: payload.delivery_instructions,
      pickupMethod: payload.pickup_method,
    });
    return { data: { orderId: order.uuid } };
  } catch (err) { translateHarperError(err); }
}

export async function scanToLink(crateId: string, shelfId: string): Promise<{
  crate: { uuid: string; shelf_id: string; last_scanned_at: string; last_scanned_by: string };
  log: { uuid: string; crate_id: string; shelf_id: string; assigned_by: string; assigned_at: string; source: string };
}> {
  try {
    return await post<{
      crate: { uuid: string; shelf_id: string; last_scanned_at: string; last_scanned_by: string };
      log: { uuid: string; crate_id: string; shelf_id: string; assigned_by: string; assigned_at: string; source: string };
    }>('/ScanToLink', { crateId, shelfId });
  } catch (err) { translateHarperError(err); }
}

export async function getAdminCustomers(): Promise<{ data: Record<string, unknown>[]; total: number }> {
  try {
    const res = await getCollection<Record<string, unknown>>('/Customer');
    return { data: res.data, total: res.total };
  } catch (err) { translateHarperError(err); }
}

/**
 * Admin people directory — server-side role filter + substring search +
 * offset/limit pagination (see resources/AdminCustomers.js). Replaces the
 * old "load every Customer, filter in the browser" path for the list page.
 */
export async function getAdminCustomersPage(params?: {
  q?: string | undefined;
  role?: string | undefined;
  /**
   * payment_method_status filter: none | active | past_due | suspended |
   * canceled, or a comma-separated OR set. 'all' / omitted means no filter.
   * Server validates against a whitelist, so an unknown value narrows nothing.
   */
  payment?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}): Promise<{
  data: Record<string, unknown>[];
  total: number | null;
  hasMore: boolean;
  offset: number;
  limit: number;
  searchTruncated: boolean;
}> {
  const limit = Math.min(params?.limit ?? 50, 200);
  const offset = Math.max(0, params?.offset ?? 0);
  const qs = new URLSearchParams();
  if (params?.q && params.q.trim()) qs.set('q', params.q.trim());
  if (params?.role && params.role !== 'all') qs.set('role', params.role);
  if (params?.payment && params.payment !== 'all') qs.set('payment', params.payment);
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  try {
    const res = await getCollection<Record<string, unknown>>(`/AdminCustomers?${qs.toString()}`) as
      (Awaited<ReturnType<typeof getCollection<Record<string, unknown>>>> & {
        total?: number | null; hasMore?: boolean; searchTruncated?: boolean;
      });
    return {
      data: res.data ?? [],
      total: res.total ?? null,
      hasMore: !!res.hasMore,
      offset, limit,
      searchTruncated: !!res.searchTruncated,
    };
  } catch (err) { translateHarperError(err); }
}

export interface AdminCustomerDetailResponse {
  customer: Record<string, unknown>;
  crates: Record<string, unknown>[];
  orders: Record<string, unknown>[];
  subscriptions: Record<string, unknown>[];
  feedback: Record<string, unknown>[];
  error_logs: Record<string, unknown>[];
}

export async function getAdminCustomerDetail(id: string): Promise<AdminCustomerDetailResponse> {
  try {
    const res = await getOne<AdminCustomerDetailResponse>(`/AdminCustomerDetail/${id}`);
    if (!res) throw new ApiError(404, 'Customer not found');
    return res;
  } catch (err) { translateHarperError(err); }
}

/**
 * Admin-only PATCH for a narrow whitelist of Customer fields. Mirrors the
 * resource's PATCHABLE_FIELDS contract — keep them in sync when adding
 * new admin-editable flags.
 */
export async function updateAdminCustomerFields(
  id: string,
  fields: {
    test_features_enabled?: boolean;
    role?: 'customer' | 'driver' | 'admin' | 'both';
    cluster_id?: string | null;
    name?: string;
    phone?: string;
    // Changing address re-geocodes + re-resolves cluster server-side. Pass
    // lat/lng (from Places autocomplete) for precision; omitted → Nominatim.
    address?: string;
    lat?: number;
    lng?: number;
    /** Optional operator note recorded in the audit log (not stored on the customer). */
    reason?: string;
  },
): Promise<MutationResponse> {
  try {
    await patch<{ success: boolean }>(`/AdminCustomerDetail/${id}`, fields);
    return { success: true };
  } catch (err) { translateHarperError(err); }
}

// ── Admin billing actions (Stripe, audited) ───────────────────────────────

export interface AdminBillingInvoice {
  id: string;
  number: string | null;
  created: number;
  status: string | null;
  amount_paid: number;
  amount_due: number;
  amount_remaining: number;
  currency: string;
  hosted_invoice_url: string | null;
  payment_intent: string | null;
  refundable: boolean;
  retryable: boolean;
}
export interface AdminBillingData {
  stripe_customer_id: string | null;
  stripe_configured: boolean;
  /** Raw Stripe customer balance (negative = the customer holds credit). */
  balance: number;
  currency: string;
  invoices: AdminBillingInvoice[];
}

/** Admin: a customer's recent Stripe invoices + balance (read-only, live). */
export async function getAdminBilling(customerId: string): Promise<AdminBillingData> {
  try {
    const res = await getOne<AdminBillingData>(`/AdminBilling?customerId=${encodeURIComponent(customerId)}`);
    if (!res) throw new ApiError(500, 'No billing data returned');
    return res;
  } catch (err) { translateHarperError(err); }
}

/** Admin: run an audited Stripe money-movement action. Reason is required. */
export async function adminBillingAction(payload: {
  action: 'refund' | 'credit' | 'retry';
  customerId: string;
  reason: string;
  payment_intent?: string | undefined;
  amount?: number | undefined;
  invoiceId?: string | undefined;
}): Promise<{ ok: boolean } & Record<string, unknown>> {
  try {
    return await post<{ ok: boolean } & Record<string, unknown>>('/AdminBillingAction', payload);
  } catch (err) { translateHarperError(err); }
}

/**
 * Admin: close another customer's account (one-click wind-down — cancels
 * inbound orders, schedules final returns, flips account_state to 'closing',
 * stops billing at period end). super_user / admin only, server-side.
 */
export async function closeCustomerAccount(
  customerId: string,
  opts?: { scheduledDate?: string; timeWindow?: string },
): Promise<{ account_state?: string; closure_orders?: unknown }> {
  try {
    return await post('/CloseAccount', { customerId, ...opts });
  } catch (err) { translateHarperError(err); }
}

/** Admin: reverse an in-progress closure for another customer. */
export async function reopenCustomerAccount(
  customerId: string,
): Promise<{ ok: boolean; account_state?: string; already?: boolean }> {
  try {
    return await post('/ReopenAccount', { customerId });
  } catch (err) { translateHarperError(err); }
}

// ── Dev / test affordances ──────────────────────────────────────────────

export interface SimulateScanResponse {
  crateId?: string;
  shelfId?: string;
}

/**
 * Dev-only: resolve the QR the driver WOULD decode at their current stop
 * and return it. The caller then feeds the id back into the normal
 * submitCrateScan / submitShelfScan handler so the real ValidateCrateScan
 * mutation runs. Gated server-side by Customer.test_features_enabled.
 */
export async function simulateScan(
  body: { mode: 'crate' | 'shelf'; pendingCrateId?: string },
): Promise<SimulateScanResponse> {
  try {
    const res = await post<SimulateScanResponse>('/DevSimulateScan', body);
    return res;
  } catch (err) { translateHarperError(err); }
}

// ── Health ──────────────────────────────────────────────────────────────

/**
 * Persist the device's FCM registration token to the caller's Customer row.
 * Idempotent on the server — repeats with an unchanged token are a no-op.
 * Called by the native push bridge on auth+token-ready.
 */
export async function registerFcmToken(params: { token: string }): Promise<{ ok: boolean; changed: boolean }> {
  return await post<{ ok: boolean; changed: boolean }>('/RegisterFcmToken', params);
}

/**
 * Harper has no dedicated /health endpoint. Treat a successful GET against
 * any cheap collection as "server reachable + auth valid". If auth isn't
 * configured yet, we probe without credentials and just verify TCP/TLS.
 */
export async function checkHealth(): Promise<{ status: string; timestamp: string }> {
  const res = await fetch(`${HARPER_BASE_URL}/`, { method: 'GET' });
  if (!res.ok && res.status !== 401) {
    throw new ApiError(res.status, res.statusText);
  }
  return { status: 'ok', timestamp: new Date().toISOString() };
}
